/**
 * Ein Monat aufgeklappt: welche Leistungen, welche Belege, welche Abweichung.
 *
 * Der Klick auf einen Balken landet hier — und zwar immer im MONAT. Frueher
 * las der Drilldown `points[mi]`, und das war bei Quartals- oder Monatsraster
 * ein Quartal bzw. ein Tag (Befund 13).
 */
import React, { useMemo, useState } from 'react';
import { Download, Loader2, Receipt } from 'lucide-react';
import { Button } from '../Button';
import type { CockpitModel, ForecastResult } from '../../services/cockpitTypes';
import { ModalShell } from './CockpitBits';
import { MO, MO_FULL, documentTypeLabel, fmtCents, fmtCentsExact } from './cockpitUi';
import { exportMonthPdf, type PdfContext } from './cockpitPdf';

const SELECT = 'h-8 px-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] outline-none cursor-pointer max-w-[220px]';
const TH = 'px-3 py-2 text-left text-[11px] font-semibold text-slate-400 uppercase';
const TD = 'px-3 py-2 text-[11px] text-slate-600';

export interface MonthDrilldownProps {
    model: CockpitModel;
    forecast: ForecastResult;
    monthIndex: number;
    hideAmounts: boolean;
    pdfContext: PdfContext;
    onMonth: (index: number) => void;
    onClose: () => void;
}

export const MonthDrilldown: React.FC<MonthDrilldownProps> = ({
    model, forecast, monthIndex, hideAmounts, pdfContext, onMonth, onClose,
}) => {
    const [projectFilter, setProjectFilter] = useState('all');
    const [employeeFilter, setEmployeeFilter] = useState('all');
    const [exporting, setExporting] = useState(false);

    const monthName = MO_FULL[monthIndex];
    const money = (c: number) => fmtCents(c, hideAmounts);
    // Die Engine liefert Einzelheiten fuer alle zwoelf Monate. Der Dialog laesst
    // sich deshalb auch auf einen Monat ausserhalb des gewaehlten Zeitraums
    // blaettern — dann sagt er das, statt eine Zahl zu zeigen, die in keiner
    // Kachel auftaucht.
    const inPeriod = model.periodMonths.includes(monthIndex);

    const monthPerformed = useMemo(
        () => model.performedDetails.filter((d) => d.month === monthIndex),
        [model.performedDetails, monthIndex],
    );
    const monthBilled = useMemo(
        () => model.billedDetails.filter((d) => d.month === monthIndex),
        [model.billedDetails, monthIndex],
    );

    const projectOptions = useMemo(() => {
        const map = new Map<string, string>();
        monthPerformed.forEach((d) => map.set(d.projectId, d.projectName));
        monthBilled.forEach((d) => { if (d.projectId) map.set(d.projectId, d.projectName); });
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [monthPerformed, monthBilled]);

    const employeeOptions = useMemo(() => {
        const map = new Map<string, string>();
        monthPerformed.forEach((d) => { if (d.performerId) map.set(d.performerId, d.performerName); });
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [monthPerformed]);

    const performed = useMemo(() => monthPerformed.filter((d) =>
        (projectFilter === 'all' || d.projectId === projectFilter)
        && (employeeFilter === 'all' || d.performerId === employeeFilter)),
    [monthPerformed, projectFilter, employeeFilter]);

    // Belege tragen keine ausfuehrende Person — der Mitarbeiterfilter greift
    // dort nicht und wird darum auch nicht so dargestellt.
    const billed = useMemo(() => monthBilled.filter((d) =>
        projectFilter === 'all' || d.projectId === projectFilter),
    [monthBilled, projectFilter]);

    const performedSum = performed.reduce((a, d) => a + d.cents, 0);
    const billedSum = billed.reduce((a, d) => a + d.cents, 0);
    const forecastSum = model.months[monthIndex]?.forecastCents ?? 0;

    const variance = useMemo(() => {
        const names = new Map(model.projects.map((p) => [p.id, p.name]));
        const rows = new Map<string, { name: string; forecast: number; performed: number }>();
        Object.entries(forecast.byProject).forEach(([pid, series]) => {
            const value = series[monthIndex] ?? 0;
            if (value === 0) return;
            // Die Prognose kennt ALLE Projekte, das Modell nur die gefilterten.
            // Ohne diese Schranke stuenden unter einem Mitarbeiterfilter fremde
            // Projekte als nackte UUID in der Tabelle und die Summe widerspraeche
            // der Kachel (`months[m].forecastCents`).
            if (!names.has(pid)) return;
            if (projectFilter !== 'all' && pid !== projectFilter) return;
            rows.set(pid, { name: names.get(pid) ?? pid, forecast: value, performed: 0 });
        });
        performed.forEach((d) => {
            const row = rows.get(d.projectId) ?? { name: d.projectName, forecast: 0, performed: 0 };
            row.performed += d.cents;
            rows.set(d.projectId, row);
        });
        return [...rows.values()]
            .map((r) => ({ ...r, diff: r.performed - r.forecast }))
            .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
    }, [forecast.byProject, model.projects, monthIndex, performed, projectFilter]);

    const runExport = async () => {
        setExporting(true);
        try {
            await exportMonthPdf({ model, forecast, monthIndex, ctx: pdfContext, projectFilter, employeeFilter });
        } catch (e) {
            console.error(e);
            alert('PDF-Export fehlgeschlagen.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <ModalShell
            title={`${monthName} ${model.period.year}`}
            width="max-w-4xl"
            onClose={onClose}
            subtitle={
                <>
                    <span className="text-[11px] text-sky-300">Forecast {money(forecastSum)}</span>
                    <span className="text-[11px] text-emerald-300">Erbracht {money(performedSum)}</span>
                    <span className="text-[11px] text-violet-300">Abgerechnet {money(billedSum)}</span>
                    <span className={`text-[11px] font-semibold ${performedSum - forecastSum >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                        Abweichung {performedSum - forecastSum >= 0 ? '+' : ''}{money(performedSum - forecastSum)}
                    </span>
                    {!inPeriod && (
                        <span className="text-[11px] text-amber-300">außerhalb des gewählten Zeitraums</span>
                    )}
                </>
            }
            footer={
                <>
                    <div className="flex gap-2">
                        {monthIndex > 0 && (
                            <button onClick={() => onMonth(monthIndex - 1)} className="px-3 py-1.5 text-[11px] font-semibold text-slate-500 hover:text-blue-600 bg-white border border-slate-200 rounded-lg">
                                ← {MO[monthIndex - 1]}
                            </button>
                        )}
                        {monthIndex < 11 && (
                            <button onClick={() => onMonth(monthIndex + 1)} className="px-3 py-1.5 text-[11px] font-semibold text-slate-500 hover:text-blue-600 bg-white border border-slate-200 rounded-lg">
                                {MO[monthIndex + 1]} →
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={runExport}
                            disabled={exporting}
                            icon={exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        >
                            PDF
                        </Button>
                        <Button variant="primary" size="sm" onClick={onClose}>Schließen</Button>
                    </div>
                </>
            }
        >
            <div className="flex flex-wrap items-center gap-2">
                <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className={SELECT}>
                    <option value="all">Alle Projekte</option>
                    {projectOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
                <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} className={SELECT} title="Wirkt nur auf erbrachte Leistungen">
                    <option value="all">Alle Ausführenden</option>
                    {employeeOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
            </div>

            <section>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">
                    Erbrachte Leistungen <span className="text-[11px] text-slate-400 font-normal">({performed.length}) · Σ {fmtCentsExact(performedSum, hideAmounts)}</span>
                </h4>
                {performed.length === 0 ? (
                    <div className="py-6 text-center text-slate-400 text-[11px] border border-slate-100 rounded-xl">Keine Leistungen in diesem Monat</div>
                ) : (
                    <div className="overflow-x-auto border border-slate-100 rounded-xl">
                        <table className="w-full">
                            <thead className="bg-slate-50"><tr>
                                <th className={TH}>Datum</th><th className={TH}>Projekt</th><th className={TH}>Leistung</th>
                                <th className={`${TH} text-right`}>Menge</th><th className={`${TH} text-right`}>Betrag</th><th className={TH}>Ausführend</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-50">
                                {[...performed].sort((a, b) => a.date.localeCompare(b.date)).map((d, i) => (
                                    <tr key={`${d.projectId}-${d.serviceId}-${d.reference}-${i}`} className="hover:bg-slate-50/60">
                                        <td className={TD}>{d.date.slice(8, 10)}.{d.date.slice(5, 7)}.</td>
                                        <td className={`${TD} font-semibold text-slate-700`}>{d.projectName}</td>
                                        <td className={TD}>
                                            {d.serviceTitle}
                                            {d.note && <div className="text-[11px] text-slate-400">{d.note}</div>}
                                        </td>
                                        <td className={`${TD} text-right`}>{d.quantity} {d.unit}</td>
                                        <td className={`${TD} text-right font-semibold text-emerald-700`}>{fmtCentsExact(d.cents, hideAmounts)}</td>
                                        <td className={TD}>{d.performerName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section>
                <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-violet-500" />
                    Belege <span className="text-[11px] text-slate-400 font-normal">({billed.length}) · Σ {fmtCentsExact(billedSum, hideAmounts)}</span>
                </h4>
                {billed.length === 0 ? (
                    <div className="py-6 text-center text-slate-400 text-[11px] border border-slate-100 rounded-xl">Keine Belege in diesem Monat</div>
                ) : (
                    <div className="overflow-x-auto border border-slate-100 rounded-xl">
                        <table className="w-full">
                            <thead className="bg-slate-50"><tr>
                                <th className={TH}>Datum</th><th className={TH}>Beleg</th><th className={TH}>Art</th>
                                <th className={TH}>Projekt</th><th className={TH}>Auftraggeber</th><th className={`${TH} text-right`}>Netto</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-50">
                                {[...billed].sort((a, b) => a.date.localeCompare(b.date)).map((d) => (
                                    <tr key={d.invoiceId} className="hover:bg-slate-50/60">
                                        <td className={TD}>{d.date.slice(8, 10)}.{d.date.slice(5, 7)}.</td>
                                        <td className={`${TD} font-semibold text-slate-700`}>{d.invoiceNumber}</td>
                                        <td className={TD}>{documentTypeLabel(d.documentType)}</td>
                                        <td className={TD}>{d.projectName}</td>
                                        <td className={TD}>{d.customerName}</td>
                                        <td className={`${TD} text-right font-semibold ${d.cents < 0 ? 'text-red-600' : 'text-violet-700'}`}>
                                            {fmtCentsExact(d.cents, hideAmounts)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {variance.length > 0 && (
                <section>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">Abweichung nach Projekt</h4>
                    <div className="space-y-1">
                        {variance.slice(0, 20).map((r) => (
                            <div key={r.name} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-100">
                                <span className="text-[11px] text-slate-700 flex-1 truncate">{r.name}</span>
                                <span className="text-[11px] text-sky-600 shrink-0">F {money(r.forecast)}</span>
                                <span className="text-[11px] text-emerald-600 shrink-0">E {money(r.performed)}</span>
                                <span className={`text-[11px] font-semibold shrink-0 w-24 text-right ${r.diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {r.diff >= 0 ? '+' : ''}{money(r.diff)}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </ModalShell>
    );
};
